import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import AuthService from '@/core/services/AuthService';
import { TokenCaptureUtils } from '@/core/utils/tokenCapture';
import { User } from '@/core/services/UserService';

export interface AuthState {
  hasInitialData: boolean;
  activeUser: User | null;
}

export const useAuth = () => {
  const { showToast } = useToast();
  const [authState, setAuthState] = useState<AuthState>({
    hasInitialData: false,
    activeUser: null
  });

  const loadUserData = useCallback(async (): Promise<void> => {
    try {
      const { default: UserService } = await import('@/core/services/UserService');
      const userData = await UserService.getUserMe();
      setAuthState(prev => ({ ...prev, activeUser: userData }));
    } catch (error) {
      console.error('User API failed, using default:', error);
      showToast({
        variant: 'error',
        title: 'Erro ao carregar usuário',
        text: 'Ocorreu um erro ao carregar os dados do usuário. Tente novamente.',
        icon: 'error',
      });
    }
  }, [showToast]);

  const handleAuthentication = useCallback(async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, hasInitialData: false }));
      TokenCaptureUtils.captureAndStoreTokens();
      const authenticated = AuthService.isAuthenticated();
      if (!authenticated) {
        return AuthService.sendToLoginPage();
      }
      await loadUserData();
      setAuthState(prev => ({ ...prev, hasInitialData: true }));
    } catch (error) {
      console.error('Authentication error:', error);
      setAuthState({ hasInitialData: false, activeUser: null });
      showToast({
        variant: 'error',
        title: 'Erro de autenticação',
        text: 'Ocorreu um erro durante a autenticação. Tente novamente.',
        icon: 'error',
      });
    }
  }, [loadUserData, showToast]);


  const logout = (): void => {
    AuthService.logout();
  };

 
  useEffect(() => {
    const initialize = async () => {
      await handleAuthentication();
    };
    
    initialize();
  }, [handleAuthentication]);

  return {
    ...authState,
    logout,
    handleAuthentication
  };
};